/**
 * Tasks: the personal and Patient work list, its editor and reminders.
 *
 * Every refusal the Task API can answer with gets its own sentence, because a
 * generic failure is what makes a clinician reload instead of act. The
 * SMTP acceptance is deliberately distinct from final delivery.
 *
 * A COUNT IS TWO IDS, never one string with a bracketed plural. `{count}
 * task(s)` is a sentence no language speaks, and Hebrew does not form its
 * plural by adding a letter to the singular — so the count picks its own id the
 * way the Patient registry's does.
 *
 * AN ACTION BOUND TO ONE TASK NAMES IT THROUGH A PLACEHOLDER. The named ids
 * below keep punctuation and word order in the catalogue rather than in a
 * component.
 */
export const tasks = {
  'task.title': 'Tasks',
  'task.loading': 'Loading tasks',
  'task.loadFailed': 'Tasks could not be loaded.',
  'task.empty': 'No tasks yet',
  'task.emptyDetail': 'Tasks you or a colleague create appear here.',
  'task.filteredEmpty': 'No tasks match these filters',
  'task.filteredEmptyDetail': 'Change or clear the filters to see more.',
  'task.new': 'New task',
  'task.newForEvent': 'New task',
  'task.editHeading': 'Edit task',
  'task.editNamed': 'Edit: {title}',
  'task.save': 'Save task',
  'task.create': 'Create task',
  'task.complete': 'Complete',
  'task.completeNamed': 'Complete: {title}',
  'task.cancelTask': 'Cancel task',
  'task.fieldTitle': 'Title',
  'task.details': 'Details (optional)',
  'task.noDetails': 'No details',
  'task.dueAt': 'Due',
  'task.noDue': 'No due time',
  'task.dueOverdue': 'Overdue',
  'task.dueToday': 'Due today',
  'task.remindAt': 'Remind at (optional)',
  'task.repeat': 'Repeat',
  'task.repeat.none': 'Does not repeat',
  'task.repeat.daily': 'Daily',
  'task.repeat.weekly': 'Weekly',
  'task.repeat.monthly': 'Monthly',
  'task.priority': 'Priority',
  'task.priority.low': 'Low',
  'task.priority.normal': 'Normal',
  'task.priority.high': 'High',
  'task.status.open': 'Open',
  'task.status.inProgress': 'In progress',
  'task.status.completed': 'Completed',
  'task.status.cancelled': 'Cancelled',
  'task.assignee': 'Responsible clinician',
  'task.patient': 'Patient (optional)',
  'task.scheduleEvent': 'Schedule event (optional)',
  'task.noPatient': 'No patient',
  'task.noScheduleEvent': 'No schedule event',
  'task.linkedScheduleEvent': 'Linked schedule event · {date}',
  'task.notAnAppointment':
    'A task on a schedule event is not an appointment: it reserves no time and holds no availability.',
  'task.unsavedIntent': 'Task \u2014 edits that were never saved',
  'task.notify': 'Send reminders for this task',
  'task.notifyDetail': 'A task can exist without any message. Turn this on only when someone should be reminded.',
  'task.messageText': 'Reminder text',
  'task.recipients': 'Recipients',
  'task.recipientsHint': 'Hold Ctrl or Cmd to choose more than one.',
  'task.titleRequired': 'A title is required.',
  'task.assigneeRequired': 'One responsible clinician is required.',
  'task.messageIncomplete': 'Reminders need text and at least one recipient.',
  'task.contextMismatch': 'That schedule event belongs to a different patient.',
  'task.contextEventOption': '{event}, {patient}',
  'task.staleRevision':
    'This task changed elsewhere. Your text is kept; reload it to see the current revision before saving.',
  'task.notActive': 'This task is completed or cancelled and can no longer change.',
  'task.rejected': 'The task was refused as entered. Check the fields and try again.',
  'task.gone': 'This task is no longer available.',
  'task.saveFailed': 'The task could not be saved. Try again.',
  // The registry's own columns. Each heading names the fact its funnel
  // narrows, so the word above the cells and the word on the popover agree.
  'task.tableLabel': 'Tasks',
  'task.columnTitle': 'Task',
  'task.columnStatus': 'Status',
  'task.columnPriority': 'Priority',
  'task.columnDue': 'Due',
  'task.columnAssignee': 'Responsible',
  'task.columnPatient': 'Patient',
  'task.columnActions': 'Actions',
  // In the Responsible funnel, where it narrows: it is a question about that
  // column and about nothing else.
  'task.filterAssignedToMe': 'Assigned to me',
  'task.patientNoMatches': 'No patients match',
  'task.start': 'Start work',
  'task.returnToOpen': 'Return to open',
  'task.scheduleContext': 'Schedule event',
  'task.resultCountOne': '{count} task',
  'task.resultCountMany': '{count} tasks',
  'task.simulate': 'Simulate now',
  'task.simulateNamed': 'Simulate now: {title}',
  'task.simulating': 'Simulating…',
  'task.simulateExplains':
    'This runs the selected channels as a SIMULATION. Nothing is sent and no provider is contacted.',
  'task.simulationNotConfigured': 'This task has no reminder configured, so there is nothing to simulate.',
  'task.simulationConflict': 'That simulation was already recorded.',
  'task.reminderConfigured': 'Reminder configured',
  'task.routingPreferences': 'Delivery follows each recipient’s communication preferences.',
  'task.attempts': 'Reminder attempts',
  'task.attemptsNamed': 'Reminder attempts: {title}',
  'task.attemptsEmpty': 'No attempts yet',
  'task.attemptsFailed': 'Reminder attempts could not be loaded.',
  'task.attemptTrigger.manual': 'Manual',
  'task.attemptTrigger.automatic': 'Automatic',
  'task.remindersTitle': 'Reminders due today',
  'task.remindersEmpty': 'No reminders due on this day',
  'task.scheduleCountOne': '{count} task',
  'task.scheduleCountMany': '{count} tasks',
  'task.scheduleRowTasksOne': '{count} task for {event}',
  'task.scheduleRowTasksMany': '{count} tasks for {event}',
  'task.scheduleUnknown': 'Tasks unavailable',
  'task.scheduleLoadFailed': 'Reminders could not be loaded. The appointments above are unaffected.',
  'task.openTask': 'Open task {title}',
} as const
